import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Plus, X, ChatCircle } from "@phosphor-icons/react";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    const { data } = await api.get("/tickets");
    setTickets(data);
  };
  useEffect(() => { load(); }, []);

  const openTicket = async (t) => {
    setActive(t);
    const { data } = await api.get(`/tickets/${t.id}`);
    setDetail(data);
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", { subject, body, priority: "normal" });
      toast.success("Ticket created");
      setSubject(""); setBody(""); setShowForm(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/tickets/${active.id}/reply`, { body: reply });
      setReply("");
      openTicket(active);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const close = async () => {
    await api.post(`/tickets/${active.id}/close`);
    toast.success("Ticket closed");
    setActive(null); setDetail(null);
    load();
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Support</div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Help center</h1>
        </div>
        <button data-testid="new-ticket-btn" onClick={() => setShowForm(!showForm)} className="rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-bold px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200"><Plus size={16} weight="bold" /> New ticket</button>
      </div>

      {showForm && (
        <form onSubmit={submitTicket} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5 space-y-3">
          <input data-testid="ticket-subject-input" required placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
          <textarea data-testid="ticket-body-input" required rows="4" placeholder="Describe your issue…" value={body} onChange={(e) => setBody(e.target.value)} className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
          <button data-testid="ticket-submit-btn" className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-5 py-2 transition-colors duration-200">Submit ticket</button>
        </form>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 space-y-2">
          {tickets.length === 0 && <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-6 text-center text-slate-500">No tickets yet.</div>}
          {tickets.map((t) => (
            <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => openTicket(t)} className={`w-full text-left rounded-2xl border p-4 transition-colors duration-200 ${active?.id === t.id ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">{t.subject}</div>
                  <div className="text-xs text-slate-500">{new Date(t.updated_at).toLocaleString()}</div>
                </div>
                <span className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${t.status === "closed" ? "bg-slate-100 text-slate-500 dark:bg-slate-800" : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"}`}>{t.status}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="md:col-span-2">
          {!active && <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-slate-500"><ChatCircle size={40} weight="light" className="mx-auto mb-3" /> Select a ticket to view messages.</div>}
          {active && detail && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col" style={{ minHeight: 500 }}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                <div>
                  <div className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{detail.ticket.subject}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">{detail.ticket.status}</div>
                </div>
                {detail.ticket.status !== "closed" && <button data-testid="close-ticket-btn" onClick={close} className="rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 font-bold px-3 py-1.5 text-sm inline-flex items-center gap-1"><X size={14} weight="bold" /> Close</button>}
              </div>
              <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                {detail.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.is_admin ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-md rounded-2xl px-4 py-2 text-sm ${m.is_admin ? "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100" : "bg-blue-600 text-white"}`}>
                      <div className="text-xs font-bold uppercase tracking-widest opacity-70">{m.is_admin ? "Support" : "You"}</div>
                      <div className="whitespace-pre-wrap">{m.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              {detail.ticket.status !== "closed" && (
                <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex gap-2">
                  <input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" className="flex-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-2 text-slate-900 dark:text-slate-100" />
                  <button data-testid="ticket-send-btn" onClick={sendReply} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 inline-flex items-center gap-1 transition-colors duration-200"><PaperPlaneTilt size={16} weight="fill" /> Send</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
